import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTaxYear } from "../contexts/TaxYearContext";
import {
  Users, ChevronDown, ChevronUp, Plus, Save, Trash2, Ban, CheckCircle2,
  RefreshCw, Gift, Eye, EyeOff, AlertTriangle, Loader2, Info, Calendar, Mail,
  Filter, Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const ENTITY_TYPES = [
  { value: "center", label: "Childcare Center" },
  { value: "group_home", label: "Group Child Care Home" },
  { value: "family_home", label: "Family Home" },
];

const LOCATION_TYPES = [
  { value: "commercial", label: "Commercial" },
  { value: "residence", label: "Residence" },
];

const SPONSORED_TIERS = [
  { value: "sponsored_basic", label: "Sponsored Basic — 100 pages" },
  { value: "sponsored_premium", label: "Sponsored Premium — 350 pages" },
  { value: "sponsored_premium_plus", label: "Sponsored Premium Plus — 1,000 pages" },
];

const TIER_LABEL = {
  sponsored_basic: "Sponsored Basic",
  sponsored_premium: "Sponsored Premium",
  sponsored_premium_plus: "Sponsored Premium Plus",
};

const fmtN = (n) => new Intl.NumberFormat("en-US").format(n || 0);

// Renewal-countdown helper — picks dot color + label from license_expires_at /
// trial_end. Returns {dot, color, label, daysRemaining, isUrgent, kind}.
//   kind: 'lifetime' | 'disabled' | 'trial' | 'paid' | 'unknown'
//   dot:  CSS bg-* class for a 2x2 colored dot
//   color: full text/bg classes for the pill
//   isUrgent: true when amber/red — UI uses this to show the Send Reminder button
function renewalStatus(acct) {
  if (acct.is_disabled) {
    return { kind: "disabled", dot: "bg-slate-400", color: "bg-slate-100 text-slate-600", label: "Disabled", daysRemaining: null, isUrgent: false };
  }
  // Sponsored owners with no expiry → treat as Lifetime (signature-style)
  if (!acct.license_expires_at && !acct.trial_end && acct.is_paid) {
    return { kind: "lifetime", dot: "bg-purple-500", color: "bg-purple-100 text-purple-700", label: "Lifetime", daysRemaining: null, isUrgent: false };
  }
  const expIso = acct.license_expires_at || acct.trial_end;
  if (!expIso) {
    return { kind: "unknown", dot: "bg-slate-300", color: "bg-slate-100 text-slate-500", label: "No expiry", daysRemaining: null, isUrgent: false };
  }
  const exp = new Date(expIso);
  if (Number.isNaN(exp.getTime())) {
    return { kind: "unknown", dot: "bg-slate-300", color: "bg-slate-100 text-slate-500", label: "Bad date", daysRemaining: null, isUrgent: false };
  }
  const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  const fmtDate = exp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const isTrial = !acct.is_paid && acct.trial_end;
  if (days < 0) {
    return { kind: isTrial ? "trial" : "paid", dot: "bg-red-500", color: "bg-red-100 text-red-700", label: `Expired ${fmtDate}`, daysRemaining: days, isUrgent: true };
  }
  if (days < 30) {
    const prefix = isTrial ? "Trial ends" : "Renews";
    return { kind: isTrial ? "trial" : "paid", dot: "bg-red-500", color: "bg-red-100 text-red-700", label: `${prefix} ${fmtDate} (${days}d)`, daysRemaining: days, isUrgent: true };
  }
  if (days <= 60) {
    const prefix = isTrial ? "Trial ends" : "Renews";
    return { kind: isTrial ? "trial" : "paid", dot: "bg-amber-500", color: "bg-amber-100 text-amber-800", label: `${prefix} ${fmtDate} (${days}d)`, daysRemaining: days, isUrgent: true };
  }
  return { kind: isTrial ? "trial" : "paid", dot: "bg-emerald-500", color: "bg-emerald-100 text-emerald-700", label: `Renews ${fmtDate} (${days}d)`, daysRemaining: days, isUrgent: false };
}

// Human-readable "X days ago" / "today" formatter for last_renewal_reminder_at.
function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
}

const EMPTY_FORM = {
  name: "", email: "", center_name: "", entity_state: "PA",
  entity_type: "center", location_type: "commercial",
  plan_tier: "sponsored_basic", password: "",
};

export default function CustomerPortal() {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [expanded, setExpanded] = useState(null); // user id of expanded row
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPass, setShowPass] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editDraft, setEditDraft] = useState({}); // keyed by user id
  const [saving, setSaving] = useState(null);
  const [grantPages, setGrantPages] = useState({}); // keyed by user id
  const [confirming, setConfirming] = useState(null); // user id awaiting delete confirm
  const [busyId, setBusyId] = useState(null);
  // Sprint 2 Batch 4 — filter state
  const [filterStatus, setFilterStatus] = useState("all"); // all|sponsored|trial|paid|expired|pending_deletion

  // Phase B — every page balance / grant / reset in this portal is scoped to
  // ONE tax year. Defaults to the tax year selected in the top bar.
  const { taxYear, availableYears } = useTaxYear();
  const [portalTaxYear, setPortalTaxYear] = useState(taxYear);
  useEffect(() => { setPortalTaxYear(taxYear); }, [taxYear]);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/accounts`, {
        params: { show_all: true, tax_year: portalTaxYear },
        withCredentials: true,
      });
      setAccounts(data.accounts || []);
    } catch (err) {
      toast.error("Could not load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  }, [portalTaxYear]);

  // Client-side filter
  const filteredAccounts = accounts.filter(a => {
    if (filterStatus === "all") return true;
    if (filterStatus === "sponsored") return a.account_type?.startsWith("sponsored");
    if (filterStatus === "pending_deletion") return a.deletion_status === "pending_deletion";
    if (filterStatus === "trial") return !a.account_type?.startsWith("sponsored") && !a.is_paid && !!a.trial_end;
    if (filterStatus === "paid") return a.is_paid;
    if (filterStatus === "expired") {
      if (a.trial_end) return new Date(a.trial_end) < new Date();
      if (a.license_expires_at) return new Date(a.license_expires_at) < new Date();
      return false;
    }
    return true;
  });

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const toggleRow = (id) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      const acct = accounts.find(a => a.id === id);
      if (acct) {
        setEditDraft(d => ({
          ...d,
          [id]: {
            center_name: acct.center_name,
            entity_type: acct.entity_type,
            location_type: acct.location_type,
            plan_tier: acct.plan_tier,
          }
        }));
      }
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.center_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/accounts`, form, { withCredentials: true });
      toast.success(`Sponsored account created for ${form.email}`);
      setForm(EMPTY_FORM);
      await loadAccounts();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create account";
      toast.error(typeof msg === "string" ? msg : msg.message || "Error");
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (id) => {
    setSaving(id);
    try {
      await axios.patch(`${API}/admin/accounts/${id}`, editDraft[id] || {}, { withCredentials: true });
      toast.success("Account updated");
      await loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(null);
    }
  };

  const handleGrantPages = async (id) => {
    const pages = parseInt(grantPages[id] || 0, 10);
    if (!pages || pages <= 0) { toast.error("Enter a valid page count"); return; }
    setBusyId(`grant-${id}`);
    try {
      const { data } = await axios.patch(`${API}/admin/accounts/${id}/grant-pages`, { pages, tax_year: portalTaxYear }, { withCredentials: true });
      toast.success(data.message);
      setGrantPages(p => ({ ...p, [id]: "" }));
      await loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant pages");
    } finally {
      setBusyId(null);
    }
  };

  const handleResetUsage = async (id) => {
    setBusyId(`reset-${id}`);
    try {
      await axios.patch(`${API}/admin/accounts/${id}/reset-usage`, {}, { params: { tax_year: portalTaxYear }, withCredentials: true });
      toast.success(`Usage reset to 0 for Tax Year ${portalTaxYear}`);
      await loadAccounts();
    } catch (err) {
      toast.error("Reset failed");
    } finally {
      setBusyId(null);
    }
  };

  // Per-sponsored-account Grant Tax Year (signature owner only)
  const [grantYears, setGrantYears] = useState({}); // {acct_id: "2026"}
  const handleGrantYear = async (id) => {
    const y = parseInt(grantYears[id] || 0, 10);
    if (!y || y < 2000 || y > 2100) { toast.error("Enter a 4-digit year"); return; }
    setBusyId(`grant-year-${id}`);
    try {
      await axios.post(`${API}/year-end/owner-grant?tax_year=${y}&target_user_id=${id}`, {}, { withCredentials: true });
      toast.success(`Tax Year ${y} activated for this sponsored account`);
      setGrantYears(g => ({ ...g, [id]: "" }));
      await loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant tax year");
    } finally {
      setBusyId(null);
    }
  };

  // Send Renewal Reminder email to the sponsored account (Resend).
  const handleSendRenewalReminder = async (id) => {
    setBusyId(`renewal-${id}`);
    try {
      const { data } = await axios.post(
        `${API}/admin/accounts/${id}/send-renewal-reminder`,
        {},
        { withCredentials: true },
      );
      const days = data.days_remaining;
      toast.success(
        days != null && days < 0
          ? "Renewal reminder sent (subscription already expired)."
          : `Renewal reminder sent (${days}d remaining).`,
      );
      await loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleDisable = async (id) => {
    setBusyId(`disable-${id}`);
    try {
      const { data } = await axios.patch(`${API}/admin/accounts/${id}/disable`, {}, { withCredentials: true });
      toast.success(data.message);
      await loadAccounts();
    } catch (err) {
      toast.error("Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    setBusyId(`delete-${id}`);
    try {
      await axios.delete(`${API}/admin/accounts/${id}`, { withCredentials: true });
      toast.success("Account and all data permanently deleted");
      setExpanded(null);
      setConfirming(null);
      await loadAccounts();
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}>
          <Users className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Customer Portal</h1>
          <p className="text-sm text-slate-500">Create and manage sponsored accounts</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        {[
          { id: "all", label: `All (${accounts.length})` },
          { id: "sponsored", label: "Sponsored" },
          { id: "trial", label: "Trial" },
          { id: "paid", label: "Paid" },
          { id: "expired", label: "Expired" },
          { id: "pending_deletion", label: "Pending Deletion" },
        ].map(f => (
          <button
            key={f.id}
            data-testid={`portal-filter-${f.id}`}
            onClick={() => setFilterStatus(f.id)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              filterStatus === f.id
                ? f.id === "pending_deletion"
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-violet-600 text-white border-violet-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Create Account Card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Plus className="w-4 h-4 text-violet-600" />
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Create Sponsored Account</h2>
        </div>
        <form onSubmit={handleCreate} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name *">
              <input
                data-testid="create-name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Jane Smith"
                className="input-base"
              />
            </Field>
            <Field label="Email Address *">
              <input
                data-testid="create-email"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane@example.com"
                className="input-base"
              />
            </Field>
            <Field label="Center / Business Name *">
              <input
                data-testid="create-center-name"
                value={form.center_name}
                onChange={e => setForm(f => ({ ...f, center_name: e.target.value }))}
                placeholder="Happy Kids Learning Center"
                className="input-base"
              />
            </Field>
            <Field label="State">
              <select data-testid="create-state" value={form.entity_state} onChange={e => setForm(f => ({ ...f, entity_state: e.target.value }))} className="input-base">
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Entity Type">
              <select data-testid="create-entity-type" value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))} className="input-base">
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Location Type">
              <select data-testid="create-location-type" value={form.location_type} onChange={e => setForm(f => ({ ...f, location_type: e.target.value }))} className="input-base">
                {LOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Sponsored Tier">
              <select data-testid="create-tier" value={form.plan_tier} onChange={e => setForm(f => ({ ...f, plan_tier: e.target.value }))} className="input-base">
                {SPONSORED_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Temporary Password *">
              <div className="relative">
                <input
                  data-testid="create-password"
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Customer uses this to log in"
                  className="input-base pr-10"
                />
                <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="submit"
              data-testid="create-account-btn"
              disabled={creating}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? "Creating…" : "Create Sponsored Account"}
            </button>
          </div>
        </form>
      </div>

      {/* Manage Accounts */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-600" />
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Manage Accounts</h2>
            <span className="ml-1 text-xs bg-violet-100 text-violet-700 font-semibold px-2 py-0.5 rounded-full">{accounts.length}</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide" htmlFor="portal-tax-year">Tax Year</label>
            <select
              id="portal-tax-year"
              data-testid="portal-tax-year-select"
              value={portalTaxYear}
              onChange={(e) => setPortalTaxYear(Number(e.target.value))}
              className="border border-slate-300 rounded-lg px-2.5 py-1 text-sm font-medium text-slate-900 bg-white"
            >
              {(availableYears?.length ? availableYears : [portalTaxYear]).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button onClick={loadAccounts} className="text-slate-400 hover:text-slate-600 transition-colors" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loadingAccounts ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-violet-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-slate-500">Loading accounts…</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-500">No accounts yet</p>
            <p className="text-xs text-slate-400 mt-1">Create your first sponsored account using the form above</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="p-12 text-center">
            <Filter className="w-8 h-8 text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-500">No accounts match this filter</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredAccounts.map(acct => {
              const isPendingDeletion = acct.deletion_status === "pending_deletion";
              const daysLeft = isPendingDeletion && acct.deletion_permanent_after
                ? Math.max(0, Math.ceil((new Date(acct.deletion_permanent_after) - Date.now()) / 86400000))
                : null;
              return (
              <div key={acct.id}>
                {/* Row */}
                <button
                  data-testid={`account-row-${acct.id}`}
                  onClick={() => toggleRow(acct.id)}
                  className="w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${acct.is_disabled ? "bg-slate-200 text-slate-500" : "bg-violet-100 text-violet-700"}`}>
                    {(acct.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: "Manrope" }}>{acct.name}</p>
                      {acct.is_disabled && <span className="text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Disabled</span>}
                      {isPendingDeletion && (
                        <span data-testid={`pending-deletion-badge-${acct.id}`} className="inline-flex items-center gap-1 text-[10px] bg-red-100 text-red-700 font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                          <Clock className="w-3 h-3" />
                          Pending Deletion{daysLeft !== null ? ` · ${daysLeft}d` : ""}
                        </span>
                      )}
                      {!acct.account_type?.startsWith("sponsored") && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Self-Registered</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{acct.email}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-4 text-xs text-slate-500">
                    <span className="truncate max-w-[120px]">{acct.center_name}</span>
                    <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">{TIER_LABEL[acct.plan_tier] || acct.plan_tier}</span>
                    <span className="whitespace-nowrap font-medium text-slate-700">{fmtN(acct.pages_used_year)} / {fmtN(acct.pages_quota_year)} pg</span>
                    {/* Renewal pill — green/amber/red dot + date + day count */}
                    {(() => {
                      const r = renewalStatus(acct);
                      return (
                        <span
                          data-testid={`renewal-pill-${acct.id}`}
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${r.color}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${r.dot}`} />
                          {r.label}
                        </span>
                      );
                    })()}
                  </div>
                  {expanded === acct.id ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                </button>

                {/* Expanded panel */}
                {expanded === acct.id && (
                  <div className="px-6 pb-6 bg-slate-50 border-t border-slate-100 space-y-6">
                    {/* Edit fields */}
                    <div className="pt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Center / Business Name">
                        <input
                          value={editDraft[acct.id]?.center_name ?? acct.center_name}
                          onChange={e => setEditDraft(d => ({ ...d, [acct.id]: { ...d[acct.id], center_name: e.target.value } }))}
                          className="input-base"
                        />
                      </Field>
                      <Field label="Entity Type">
                        <select
                          value={editDraft[acct.id]?.entity_type ?? acct.entity_type}
                          onChange={e => setEditDraft(d => ({ ...d, [acct.id]: { ...d[acct.id], entity_type: e.target.value } }))}
                          className="input-base"
                        >
                          {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Location Type">
                        <select
                          value={editDraft[acct.id]?.location_type ?? acct.location_type}
                          onChange={e => setEditDraft(d => ({ ...d, [acct.id]: { ...d[acct.id], location_type: e.target.value } }))}
                          className="input-base"
                        >
                          {LOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Sponsored Tier">
                        <select
                          value={editDraft[acct.id]?.plan_tier ?? acct.plan_tier}
                          onChange={e => setEditDraft(d => ({ ...d, [acct.id]: { ...d[acct.id], plan_tier: e.target.value } }))}
                          className="input-base"
                        >
                          {SPONSORED_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </Field>
                    </div>

                    <button
                      data-testid={`save-account-${acct.id}`}
                      onClick={() => handleSave(acct.id)}
                      disabled={saving === acct.id}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}
                    >
                      {saving === acct.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </button>

                    <div className="border-t border-slate-200 pt-5 space-y-4">
                      {/* Pages management */}
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Page Usage</p>
                          <div className="relative group">
                            <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                            <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 w-56 bg-slate-900 text-white text-xs rounded-lg p-2.5 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                              This account cannot purchase pages. Grant pages here when they need more.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="text-sm">
                            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{fmtN(acct.pages_used_year)}</span>
                            <span className="text-slate-400 ml-1">/ {fmtN(acct.pages_quota_year)} pages</span>
                            {acct.pages_purchased_extra > 0 && (
                              <span className="ml-2 text-emerald-600 text-xs font-semibold">+{fmtN(acct.pages_purchased_extra)} extra</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-violet-500 rounded-full transition-all"
                            style={{ width: `${Math.min((acct.pages_used_year / Math.max(acct.pages_quota_year, 1)) * 100, 100)}%` }}
                          />
                        </div>
                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                          <input
                            type="number"
                            min="1"
                            placeholder="Pages to grant"
                            value={grantPages[acct.id] || ""}
                            onChange={e => setGrantPages(p => ({ ...p, [acct.id]: e.target.value }))}
                            className="input-base w-36 text-sm"
                          />
                          <button
                            data-testid={`grant-pages-${acct.id}`}
                            onClick={() => handleGrantPages(acct.id)}
                            disabled={busyId === `grant-${acct.id}`}
                            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-60"
                          >
                            {busyId === `grant-${acct.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gift className="w-3.5 h-3.5" />}
                            Grant Pages
                          </button>
                          <button
                            data-testid={`reset-usage-${acct.id}`}
                            onClick={() => handleResetUsage(acct.id)}
                            disabled={busyId === `reset-${acct.id}`}
                            className="flex items-center gap-1.5 px-4 py-2 border border-slate-300 hover:bg-slate-100 text-slate-700 text-sm font-medium rounded-lg transition-all disabled:opacity-60"
                          >
                            {busyId === `reset-${acct.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Reset Usage
                          </button>
                        </div>
                        {/* Grant Tax Year — signature owner gifts year access */}
                        <div className="mt-3 pt-3 border-t border-indigo-100">
                          <p className="text-xs font-semibold text-indigo-900 uppercase tracking-wider mb-1.5">Grant Tax Year Access</p>
                          <p className="text-xs text-slate-700 mb-2">
                            Activate any tax year for this sponsored account — no payment required. Each grant is audit-logged.
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="number"
                              min="2000"
                              max="2100"
                              placeholder="e.g. 2026"
                              value={grantYears[acct.id] || ""}
                              onChange={e => setGrantYears(g => ({ ...g, [acct.id]: e.target.value }))}
                              className="input-base w-32 text-sm"
                              data-testid={`grant-year-input-${acct.id}`}
                            />
                            <button
                              data-testid={`grant-year-${acct.id}`}
                              onClick={() => handleGrantYear(acct.id)}
                              disabled={busyId === `grant-year-${acct.id}` || !grantYears[acct.id]}
                              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-60"
                            >
                              {busyId === `grant-year-${acct.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                              Grant Tax Year
                            </button>
                          </div>
                        </div>
                        {/* Renewal Reminder — visible only when amber/red */}
                        {(() => {
                          const r = renewalStatus(acct);
                          if (!r.isUrgent) return null;
                          const lastSent = timeAgo(acct.last_renewal_reminder_at);
                          return (
                            <div className="mt-3 pt-3 border-t border-indigo-100" data-testid={`renewal-reminder-panel-${acct.id}`}>
                              <p className="text-xs font-semibold text-rose-900 uppercase tracking-wider mb-1.5">Renewal Outreach</p>
                              <p className="text-xs text-slate-700 mb-2">
                                {r.kind === "trial" ? "Trial" : "Subscription"} status:{" "}
                                <span className={`font-bold ${r.daysRemaining < 0 ? "text-red-700" : r.daysRemaining < 30 ? "text-red-700" : "text-amber-800"}`}>
                                  {r.label}
                                </span>
                                {lastSent && (
                                  <span className="text-slate-500"> · Last reminder sent {lastSent}</span>
                                )}
                              </p>
                              <button
                                data-testid={`send-renewal-${acct.id}`}
                                onClick={() => handleSendRenewalReminder(acct.id)}
                                disabled={busyId === `renewal-${acct.id}`}
                                className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-60"
                              >
                                {busyId === `renewal-${acct.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                Send Renewal Reminder
                              </button>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Danger zone */}
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <p className="text-sm font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope" }}>Account Actions</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <button
                            data-testid={`disable-account-${acct.id}`}
                            onClick={() => handleToggleDisable(acct.id)}
                            disabled={busyId === `disable-${acct.id}`}
                            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-60 ${
                              acct.is_disabled
                                ? "bg-emerald-50 border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                : "bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100"
                            }`}
                          >
                            {busyId === `disable-${acct.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                              acct.is_disabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            {acct.is_disabled ? "Re-enable Account" : "Disable Account"}
                          </button>

                          {confirming === acct.id ? (
                            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              <span className="text-xs text-red-700 font-medium">Permanently delete all data?</span>
                              <button
                                data-testid={`confirm-delete-${acct.id}`}
                                onClick={() => handleDelete(acct.id)}
                                disabled={busyId === `delete-${acct.id}`}
                                className="text-xs bg-red-600 text-white px-3 py-1 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-60"
                              >
                                {busyId === `delete-${acct.id}` ? "Deleting…" : "Yes, Delete"}
                              </button>
                              <button onClick={() => setConfirming(null)} className="text-xs text-slate-500 hover:text-slate-700 font-medium">Cancel</button>
                            </div>
                          ) : (
                            <button
                              data-testid={`delete-account-${acct.id}`}
                              onClick={() => setConfirming(acct.id)}
                              className="flex items-center gap-1.5 px-4 py-2 bg-red-50 border border-red-200 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-100 transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete Account
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .input-base {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          color: #1e293b;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
        }
        .input-base:focus {
          border-color: #6D28D9;
          box-shadow: 0 0 0 2px rgba(109,40,217,0.12);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
